import Loading from '@/components_v2/Loading';
import config from '@/Constants';
import { useAuth } from '@/hooks/Authentication/auth';
import { Collection } from '@/modules/core/domain/interfaces/ICollection';
import { QueryResponse } from '@/modules/queryfilter/domain/interfaces';
import { ManageToken } from '@/modules/security/adapters';
import { AxiosHelper } from '@/modules/shared/infra/protocols/http';
import snakeToCamel from '@/utils/snakeToCamel';
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import {
  Button,
  HStack,
  List,
  ListItem,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useQuery } from 'react-query';

import DebouncedInput from '../../DebouncedInput';
import { CollectionsListProps } from './CollectionsList.types';

const CollectionsInputList: React.FC<CollectionsListProps> = ({
  onSelect,
  selected,
  queryKey = config.COLLECTIONS_URL,
  setCollections,
}) => {
  const [page, setPage] = React.useState(1);
  const [hasNextPage, setHasNextPage] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined,
  );

  const { user } = useAuth();

  const fetchCollections = async (): Promise<QueryResponse<Collection>> => {
    const query = searchQuery ? `?search=${searchQuery}` : '';
    const requestUrl = !user
      ? `external/${queryKey}${query}`
      : `${queryKey}${query}`;

    const unauthenticatedUser = !user;

    try {
      if (unauthenticatedUser) {
        ManageToken.set(config.REPORTS_API_AUTHORIZATION_TOKEN || '');
      }

      const response = await AxiosHelper.get<QueryResponse<Collection>>({
        url: requestUrl,
      });

      if (response.statusCode !== 200) {
        throw new Error('Erro ao buscar coleções');
      }

      return snakeToCamel(response.body);
    } finally {
      if (unauthenticatedUser) {
        ManageToken.clear();
      }
    }
  };

  const { data, isLoading } = useQuery<QueryResponse<Collection>>(
    ['collections', queryKey, searchQuery],
    fetchCollections,
  );

  const [items, setItems] = React.useState<Collection[]>([]);

  React.useEffect(() => {
    if (data) {
      !searchQuery && setCollections(data.items);
      setItems(data.items);
      setHasNextPage(
        data.hasNextPage ||
          ((data as unknown) as { hasnextpage: boolean }).hasnextpage, //??
      );
    }
  }, [data]);

  const handleSelect = React.useCallback(
    (collection: Collection) => {
      if (selected && collection.id === selected.id) {
        onSelect(undefined);
        return;
      }
      onSelect(collection);
    },
    [onSelect, selected],
  );

  const onSearch = React.useCallback((value: string) => {
    if (value.length > 0) {
      setSearchQuery(value);
    } else {
      setSearchQuery(undefined);
    }
    setPage(1);
  }, []);

  return (
    <VStack
      w="50%"
      borderRight="1px"
      borderRightStyle="solid"
      pr={2}
      borderRightColor="gray.200"
    >
      <Text>Coleções</Text>
      <DebouncedInput onChange={onSearch} placeholder="Pesquisar coleção" />
      <List h="50vh" overflowY="auto" spacing={1} w="full">
        {isLoading ? (
          <Loading />
        ) : (
          items?.map((item) => (
            <ListItem
              onClick={() => handleSelect(item)}
              p={2}
              borderRadius="md"
              fontWeight="semibold"
              bg={selected && selected.id === item.id ? 'blue.100' : 'none'}
              _hover={{ bg: 'gray.100' }}
              key={item.id}
              cursor="pointer"
            >
              {item.name}
            </ListItem>
          ))
        )}
      </List>
      <Spacer />
      {/* <HStack
        w="full"
        borderTopWidth="1px"
        borderColor="gray.200"
        py={3}
        justify="center"
        spacing={2}
      >
        <Button
          onClick={() => setPage((prev) => prev - 1)}
          size="sm"
          disabled={page <= 1}
          aria-label="Go to previous page"
          _disabled={{
            bg: 'gray.50',
            cursor: 'not-allowed',
            color: 'gray.400',
          }}
          _hover={{ bg: 'gray.100' }}
        >
          <ArrowBackIcon />
        </Button>

        <Text
          fontWeight="bold"
          fontSize="md"
          aria-label={`Current page, ${page}`}
        >
          {page}
        </Text>

        <Button
          onClick={() => setPage((prev) => prev + 1)}
          size="sm"
          disabled={!hasNextPage}
          aria-label="Go to next page"
          _disabled={{
            bg: 'gray.50',
            cursor: 'not-allowed',
            color: 'gray.400',
          }}
          _hover={{ bg: 'gray.100' }}
        >
          <ArrowForwardIcon />
        </Button>
      </HStack> */}
    </VStack>
  );
};

export default CollectionsInputList;
